import { useState, useEffect } from 'react';
import { ShoppingCart, ChevronDown, Menu, X, ArrowRight, Check, Droplet, Shield, Clock, Heart, User, Mail, Phone, MapPin } from 'lucide-react';

export default function NavBar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Handle scroll effect on navbar
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Product data
  const products = [
    {
      id: 1,
      name: "Whole House Filtration System",
      price: "$1,999",
      image: "/api/placeholder/400/250",
      rating: 5,
      reviewCount: 124
    },
    {
      id: 2,
      name: "Under-Sink Advanced Filter",
      price: "$499",
      image: "/api/placeholder/400/250",
      rating: 4.8,
      reviewCount: 89
    },
    {
      id: 3,
      name: "Shower Filter System",
      price: "$129",
      image: "/api/placeholder/400/250",
      rating: 4.9,
      reviewCount: 216
    }
  ];

  // Testimonial data
  const testimonials = [
    {
      id: 1,
      text: "The difference in water quality is incredible. My skin has never felt better, and our appliances are free from limescale buildup for the first time!",
      name: "Sarah Johnson",
      location: "Brisbane",
      rating: 5,
      avatar: "/api/placeholder/64/64"
    },
    {
      id: 2,
      text: "Installation was straightforward, and the customer support team was exceptional. They guided me through every step, making the process hassle-free.",
      name: "Michael Chen",
      location: "Sydney",
      rating: 5,
      avatar: "/api/placeholder/64/64"
    },
    {
      id: 3,
      text: "Our water tastes incredible now! We've eliminated plastic bottle waste and are saving money while enjoying healthier water. Highly recommended!",
      name: "Lisa Anderson",
      location: "Melbourne",
      rating: 5,
      avatar: "/api/placeholder/64/64"
    }
  ];

  // Features data
  const features = [
    {
      id: 1,
      title: "Advanced Filtration Technology",
      description: "Our proprietary multi-stage filtration system removes 99.9% of contaminants including chlorine, heavy metals, and microplastics.",
      icon: <Droplet size={24} />,
      color: "bg-blue-500"
    },
    {
      id: 2,
      title: "Superior Longevity",
      description: "Designed to last 2-3 times longer than competing products, providing better value and reduced environmental impact.",
      icon: <Clock size={24} />,
      color: "bg-green-500"
    },
    {
      id: 3,
      title: "Health Protection",
      description: "Removes harmful contaminants while preserving beneficial minerals, resulting in healthier, better tasting water.",
      icon: <Shield size={24} />,
      color: "bg-purple-500"
    },
    {
      id: 4,
      title: "Customer Satisfaction",
      description: "With over 50,000 happy customers across Australia, our track record speaks for itself.",
      icon: <Heart size={24} />,
      color: "bg-pink-500"
    }
  ];

  // FAQ data
  const faqs = [
    {
      question: "How often do I need to replace the filters?",
      answer: "Our primary filters typically last 12-18 months, depending on your water usage and local water quality. The system includes an indicator that will let you know when it's time for a replacement."
    },
    {
      question: "Is installation included with purchase?",
      answer: "Yes, professional installation is included with all whole-house systems. For under-sink models, we provide detailed DIY installation guides, but professional installation is available for an additional fee."
    },
    {
      question: "Will a water filtration system affect my water pressure?",
      answer: "Our systems are designed to minimize pressure drop. Most customers experience no noticeable difference in water pressure after installation."
    },
    {
      question: "Are your filters certified?",
      answer: "Yes, all our filtration systems are independently tested and certified to meet or exceed Australian water quality standards."
    }
  ];

  // Water quality stats
  const waterStats = [
    { label: "Chemicals in tap water", value: "up to 50+" },
    { label: "Chlorine removal", value: "99.9%" },
    { label: "Heavy metals filtered", value: "99.8%" },
    { label: "Microplastics removed", value: "99.9%" }
  ];

  // Counter for animation
  const [count, setCount] = useState(0);
  useEffect(() => {
    const timer = setTimeout(() => {
      setCount(prevCount => (prevCount + 1) % testimonials.length);
    }, 5000);
    return () => clearTimeout(timer);
  }, [count, testimonials.length]);

  return (
     <div className=" bg-white">
      {/* Navigation */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-lg py-2' : 'bg-transparent py-4'}`}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <a href="/home" className="flex items-center">
                <div className={`${scrolled ? 'bg-blue-500' : 'bg-white'} rounded-full p-2 mr-2 transition-all duration-300`}>
                  <svg xmlns="http://www.w3.org/2000/svg" className={`h-6 w-6 ${scrolled ? 'text-white' : 'text-blue-500'} transition-all duration-300`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                </div>
                <div>
                  <h1 className={`text-xl font-bold transition-all duration-300 ${scrolled ? 'text-blue-500' : 'text-white'}`}>Home Filtaration System</h1>
                  <p className={`text-xs transition-all duration-300 ${scrolled ? 'text-gray-500' : 'text-blue-100'}`}>Pure, clean water for you</p>
                </div>
              </a>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="/" className={`transition-all duration-300 ${scrolled ? 'text-gray-800 hover:text-blue-500' : 'text-white hover:text-blue-100'}`}>Home</a>
              <div className="relative group">
                <a href="/about" className={`flex items-center transition-all duration-300 ${scrolled ? 'text-gray-800 hover:text-blue-500' : 'text-white hover:text-blue-100'}`}>
                  About us 
                </a>
                {/* <div className="absolute hidden group-hover:block bg-white shadow-lg rounded-xl p-2 mt-2 z-10 min-w-max">
                  <a href="/about" className="block px-4 py-2 text-gray-800 hover:bg-blue-50 rounded-lg">Our Story</a>
                  <a href="#" className="block px-4 py-2 text-gray-800 hover:bg-blue-50 rounded-lg">Mission</a>
                  <a href="#" className="block px-4 py-2 text-gray-800 hover:bg-blue-50 rounded-lg">Team</a>
                </div> */}
              </div>
              <div className="relative group">
                <a href="/water-filtaration" className={`flex items-center transition-all duration-300 ${scrolled ? 'text-gray-800 hover:text-blue-500' : 'text-white hover:text-blue-100'}`}>
                  Our Products 
                </a>
               
              </div>
            
              <a href="/contact" className={`transition-all duration-300 ${scrolled ? 'text-gray-800 hover:text-blue-500' : 'text-white hover:text-blue-100'}`}>Contact Us</a>
              <a href="/contact" className={`px-4 py-2 rounded-full font-medium transition-all duration-300 ${scrolled ? 'bg-blue-500 text-white hover:bg-blue-600' : 'bg-white text-blue-500 hover:bg-blue-50'}`}>
                Request a Quote
              </a>
             
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`transition-all duration-300 ${scrolled ? 'text-gray-800 hover:text-blue-500' : 'text-white hover:text-blue-100'}`}
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 bg-white rounded-xl shadow-lg p-4 animate-fadeDown">
              <a href="#" className="block text-gray-800 hover:bg-blue-50 py-2 px-4 rounded-lg">Home</a>
              <a href="#" className="block text-gray-800 hover:bg-blue-50 py-2 px-4 rounded-lg">About us</a>
              <a href="#" className="block text-gray-800 hover:bg-blue-50 py-2 px-4 rounded-lg">Our Products</a>
              <a href="#" className="block text-gray-800 hover:bg-blue-50 py-2 px-4 rounded-lg">Shop</a>
              <a href="#" className="block text-gray-800 hover:bg-blue-50 py-2 px-4 rounded-lg">Contact Us</a>
              <a href="#" className="block bg-blue-500 text-white px-4 py-2 rounded-lg font-medium mt-2">Request a Quote</a>
            </div>
          )}
        </div>
      </nav>
</div>
   
  );
}